import { UnitSystemEnum, UnitsEnum } from '@/__generated__/graphql';
import { NOT_APPLICABLE_LABEL } from '../constants';

export const METERS_TO_INCHES_FACTOR = 39.37007874;
export const INCHES_TO_METERS_FACTOR = 0.0254;
export const METERS_TO_MILLIMETERS_FACTOR = 1000;
export const MILLIMETERS_TO_METERS_FACTOR = 0.001;
export const SQFEET_TO_SQMETERS_FACTOR = 10.7639;
export const INCHES_TO_MILI_METERS = 1000;
export const MILI_METERS_TO_INCHES = 39.37007874

export const convertMetersToFeet = (meters: number): number => meters * 3.28084;
export const convertFeetToMeters = (feet: number): number => feet / 3.28084;

export const convertMetersToInches = (meters: number): number => meters * 39.37007874;
export const convertInchesToMeters = (inches: number): number => inches * 0.0254;

export const convertMetersToMillimeters = (meters: number): number => meters * 1000;
export const convertMillimetersToMeters = (mm: number): number => mm * 0.001;
export const convertSquareFeetToSquareMeters = (sqft: number): number =>
  sqft / SQFEET_TO_SQMETERS_FACTOR;

export const convertSqMetersToSqFeet = (sqm: number): number => sqm * 10.7639;
export const convertSqFeetToSqMeters = (sqft: number): number => sqft * 0.092903;

export const convertInchesToMillimeters = (inch: number): number =>
  convertInchesToMeters(inch) * 1000;
export const convertMillimetersToInches = (mm: number): number =>
  convertMillimetersToMeters(mm) * 39.37007874;

export const convertInchesToFeet = (inches: number): number => inches / 12;

export const formatDisplay = (
  value: number,
  unit: string,
  roundToDecimal: number = 2,
  showUnitLabel: boolean = true
): string => {
  const rounded = value.toFixed(roundToDecimal);
  return showUnitLabel ? `${rounded}${unit}` : rounded;
};

type LocaliseMeasurementParams = {
  unitSystem: UnitSystemEnum;
  sourceUnit?: UnitsEnum | undefined;
  value?: number | null;
  showUnitLabel?: boolean; // default: true
  roundToDecimal?: number; // default: 2
  displayAsUnit?: UnitsEnum; // optional unit to convert to before localising
  // isBlisterField?: boolean | undefined;
  // isHeight?: boolean | undefined;
  // percentage?: number | undefined;
  // inchesValue?: boolean | undefined;
  // squareMeterValue?: boolean | undefined;
  fallbackLabel?: string;
  // showPercentageInBraces?: boolean;
};

const convertAndLabel = (
  unitSystem: UnitSystemEnum,
  unit: UnitsEnum | undefined,
  value: number
): { value: number; label: string } => {
  if (!unit) return { value, label: '' };
  switch (unit) {
    case UnitsEnum.Meter:
      return unitSystem === UnitSystemEnum.Imperial
        ? { value: convertMetersToFeet(value), label: 'ft' }
        : { value, label: 'm' };

    case UnitsEnum.Millimeter:
      return unitSystem === UnitSystemEnum.Imperial
        ? { value: convertMillimetersToInches(value), label: '"' }
        : { value, label: 'mm' };

    case UnitsEnum.SquareMeter:
      return unitSystem === UnitSystemEnum.Imperial
        ? { value: convertSqMetersToSqFeet(value), label: 'ft²' }
        : { value, label: 'm²' };

    default:
      return { value, label: '' };
  }
};

export const formatInchesValue = (value: number, showUnitLabel: boolean = true): string => {
  return formatDisplay(Number(value) * METERS_TO_INCHES_FACTOR, ' in', 2, showUnitLabel);
};

export const formatPercentage = (
  percentage: number | undefined | null,
  showPercentageInBraces = true
): string => {
  let percentageText = '';

  if (percentage !== null && percentage !== undefined) {
    percentageText = showPercentageInBraces
      ? ` (${percentage.toFixed(1)}%)`
      : ` - ${percentage.toFixed(1)}%`;
  }
  return percentageText;
};

export const localiseMeasurement = ({
  unitSystem,
  sourceUnit,
  displayAsUnit, // Used if value in the db is not the value that needs to display to the user
  value,
  showUnitLabel = true, // Should we show the unit as part of the returned value
  roundToDecimal = 3,
  fallbackLabel = NOT_APPLICABLE_LABEL,
}: LocaliseMeasurementParams): string => {
  if (value === null || value === undefined) return fallbackLabel;

  let convertedValue = value;
  let finalUnit = sourceUnit;

  if (displayAsUnit && displayAsUnit !== sourceUnit) {
    if (sourceUnit === UnitsEnum.Meter && displayAsUnit === UnitsEnum.Millimeter) {
      convertedValue = convertMetersToMillimeters(value);
      finalUnit = UnitsEnum.Millimeter;
    } else if (sourceUnit === UnitsEnum.Millimeter && displayAsUnit === UnitsEnum.Meter) {
      convertedValue = convertMillimetersToMeters(value);
      finalUnit = UnitsEnum.Meter;
    }
  }

  const { value: displayValue, label: unitLabel } = convertAndLabel(
    unitSystem,
    finalUnit,
    convertedValue
  );

  return `${formatDisplay(
    displayValue,
    showUnitLabel ? ` ${unitLabel}` : '',
    roundToDecimal,
    true
  )}`;
};

export const roundNumber = (n: number, to = 2): number => {
  return Number(n.toFixed(to));
};
